<script>
import requestFunctions from '@src/functions/requestFunctions'

// Админ: «Присвоить номер» — входящий номер боту из общего пула (API BotsInboundPhoneController)
export default {
  props: ['uuid'],
  data() {
    return {
      loading: true,
      saving: false,
      error: null,
      info: null,
      candidateIndex: 0,
      result: null,
      copied: false,
    }
  },

  computed: {
    candidate() {
      if (!this.info || !this.info.candidates.length) return null
      return this.info.candidates[this.candidateIndex % this.info.candidates.length]
    },
    apiUrl() {
      return 'https://api.client.za-bota.com/v1/bots/' + this.uuid + '/inbound-phone'
    },
  },

  async mounted() {
    await this.load()
  },

  methods: {
    async request(url, params, method) {
      try {
        return await requestFunctions.sendDataAsync(url, params, method)
      } catch (e) {
        if (e && e.response && e.response.status === 403) {
          return { status: 'error', message: 'Нет доступа: присваивать номера может только администратор.' }
        }
        return { status: 'error', message: 'Сервер не ответил. Попробуйте ещё раз.' }
      }
    },

    async load() {
      this.loading = true
      this.error = null
      const resp = await this.request(this.apiUrl, {}, 'GET')
      this.loading = false
      if (!resp || resp.status !== 'success') {
        this.error = (resp && resp.message) || 'Не удалось загрузить данные бота.'
        return
      }
      this.info = resp.data
      this.candidateIndex = 0
    },

    nextCandidate() {
      this.candidateIndex = (this.candidateIndex + 1) % this.info.candidates.length
    },

    async assign() {
      const number = this.candidate.number
      if (!window.confirm('Присвоить номер ' + this.fmtPhone(number) + ' боту #' + this.info.bot.id + ' «' + this.info.bot.name + '»?\n\nНомер уйдёт из общего пула исходящих, входящие звонки на него будут приходить в этого бота.')) {
        return
      }
      this.saving = true
      this.error = null
      const resp = await this.request(this.apiUrl, { number: number }, 'POST')
      this.saving = false
      if (!resp || resp.status !== 'success') {
        this.error = (resp && resp.message) || 'Не удалось присвоить номер.'
        // номер мог уйти в работу или его занял кто-то другой — подбираем заново
        if (resp && resp.data && (resp.data.code === 'taken' || resp.data.code === 'busy')) {
          await this.load()
          this.error = resp.message
        }
        return
      }
      this.result = resp.data
      this.info.numbers = resp.data.numbers
      this.info.billing = resp.data.billing
    },

    async release(number) {
      if (!window.confirm('Отвязать номер ' + this.fmtPhone(number) + ' от бота #' + this.info.bot.id + '?\n\nВходящие звонки на этот номер перестанут приходить в бота, номер вернётся в общий пул.')) {
        return
      }
      this.saving = true
      this.error = null
      const resp = await this.request(this.apiUrl + '/release', { number: number }, 'POST')
      this.saving = false
      if (!resp || resp.status !== 'success') {
        this.error = (resp && resp.message) || 'Не удалось отвязать номер.'
        return
      }
      this.result = null
      await this.load()
    },

    async copyNumber(number) {
      const text = '+7' + number
      try {
        await navigator.clipboard.writeText(text)
      } catch (e) {
        const el = document.createElement('textarea')
        el.value = text
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
      }
      this.copied = true
      setTimeout(() => { this.copied = false }, 2000)
    },

    fmtPhone(n) {
      const d = String(n || '')
      if (d.length !== 10) return d
      return '+7 ' + d.substr(0, 3) + ' ' + d.substr(3, 3) + '-' + d.substr(6, 2) + '-' + d.substr(8, 2)
    },
    fmtDate(s) {
      if (!s) return ''
      const [date] = String(s).split(' ')
      const [y, m, d] = date.split('-')
      return d + '.' + m + '.' + y
    },
    fmtMoney(v) {
      return Number(v || 0).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    daysWord(n) {
      const a = Math.abs(n) % 100, b = a % 10
      if (a > 10 && a < 20) return 'дней'
      if (b === 1) return 'день'
      if (b > 1 && b < 5) return 'дня'
      return 'дней'
    },
  },
}
</script>

<template>
  <div class="inbound-phone">
    <div v-if="loading" class="text-center text-muted py-4">
      <b-spinner small class="mr-2"></b-spinner>Подбираю свободный номер…
    </div>

    <template v-else-if="info">
      <div class="mb-3">
        <h5 class="mt-0 mb-1">#{{ info.bot.id }} «{{ info.bot.name }}»</h5>
        <div class="text-muted">
          {{ info.bot.fl_ai ? 'ИИ-бот' : 'Бот по сценарию' }} ·
          <span :class="info.bot.fl_enabled ? 'text-success' : 'text-danger'">{{ info.bot.fl_enabled ? 'включён' : 'выключен' }}</span>
        </div>
        <div v-if="info.owner" class="text-muted">
          Клиент: <span v-if="info.owner.name">{{ info.owner.name }} · </span>{{ info.owner.phone }}
          <template v-if="info.billing.account">
            · счёт {{ info.billing.account.number || info.billing.account.id }},
            баланс {{ fmtMoney(info.billing.account.balance) }} ₽
            ({{ info.billing.account.payment_type === 'after' ? 'постоплата' : 'предоплата' }})
          </template>
        </div>
      </div>

      <b-alert v-if="!info.billing.ok" show variant="warning" class="mb-2">{{ info.billing.text }}</b-alert>
      <b-alert v-if="!info.bot.fl_enabled" show variant="warning" class="mb-2">Бот выключен — входящие не примет, пока его не включат.</b-alert>

      <div v-if="info.numbers.length" class="mb-3">
        <div class="font-weight-bold mb-1">Номера бота</div>
        <div v-for="n in info.numbers" :key="n.number" class="d-flex align-items-center justify-content-between border-bottom py-1">
          <div>
            <span class="font-weight-bold">{{ fmtPhone(n.number) }}</span>
            <span class="text-muted ml-2">транк «{{ n.trunk_name }}»</span>
          </div>
          <b-button v-if="n.releasable" size="sm" variant="outline-danger" :disabled="saving" @click="release(n.number)">Отвязать</b-button>
          <span v-else class="text-muted small">номер клиента — здесь не отвязывается</span>
        </div>
      </div>

      <b-alert v-if="error" show variant="danger">{{ error }}</b-alert>

      <div v-if="result" class="alert alert-success mb-0">
        <div class="font-size-16 mb-2">
          Номер <span class="font-weight-bold">{{ fmtPhone(result.number) }}</span> присвоен боту.
          <b-button size="sm" variant="light" class="ml-2" @click="copyNumber(result.number)">{{ copied ? 'Скопировано' : 'Скопировать' }}</b-button>
        </div>
        <div><i class="uil uil-check-circle mr-1"></i>Звонок на номер попадает в этого бота</div>
        <div><i class="uil uil-check-circle mr-1"></i>Номер привязан только к этому боту</div>
        <div><i class="uil uil-check-circle mr-1"></i>Номер убран из общего пула — с него больше не будут звонить другим клиентам</div>
        <div>
          <i :class="result.billing.ok ? 'uil uil-check-circle mr-1' : 'uil uil-exclamation-triangle mr-1 text-warning'"></i>{{ result.billing.text }}
        </div>
        <div v-if="!result.bot_enabled"><i class="uil uil-exclamation-triangle mr-1 text-warning"></i>Бот выключен — включите его, иначе входящие не пройдут</div>
        <div class="mt-2">
          Транк «{{ info.bot.id }} Входящая» {{ result.trunk_created ? 'создан' : 'уже был' }} у клиента.
          Последний шаг — позвоните на номер с мобильного и убедитесь, что бот ответил.
        </div>
      </div>

      <template v-else>
        <div v-if="!candidate" class="alert alert-danger">
          В общем пуле не осталось свободных номеров. Нужны новые номера от оператора.
        </div>

        <div v-else class="card border mb-3">
          <div class="card-body">
            <div class="text-muted mb-1">
              Предлагаю номер
              <span v-if="info.candidates.length > 1">({{ candidateIndex + 1 }} из {{ info.candidates.length }})</span>
            </div>
            <div class="d-flex align-items-center flex-wrap">
              <h3 class="my-0 mr-3">{{ fmtPhone(candidate.number) }}</h3>
              <b-button v-if="info.candidates.length > 1" size="sm" variant="white" :disabled="saving" @click="nextCandidate">
                <i class="uil uil-sync mr-1"></i>Другой номер
              </b-button>
            </div>
            <div class="text-muted mt-2">
              <template v-if="candidate.quiet_days !== null">
                Тихий {{ candidate.quiet_days }} {{ daysWord(candidate.quiet_days) }}:
                <template v-if="candidate.last_out">последний исходящий {{ fmtDate(candidate.last_out) }}</template>
                <template v-if="candidate.last_in">, последний входящий {{ fmtDate(candidate.last_in) }}</template>
                <template v-if="candidate.released_at">, был у другого бота до {{ fmtDate(candidate.released_at) }}</template>
              </template>
              <template v-else>За {{ info.window_days }} дней звонков с номера не было</template>
            </div>
            <div class="text-muted">
              {{ candidate.in_calls > 0 ? 'Входящие на него уже приходили — оператор доставляет.' : 'Входящих на него ещё не было — проверьте звонком после привязки.' }}
            </div>
          </div>
        </div>

        <div v-if="candidate" class="text-muted small mb-3">
          Номер уйдёт из общего пула исходящих ({{ info.pool_free }} свободно) и встанет в транк клиента «{{ info.bot.id }} Входящая».
          Входящие звонки на него будут приходить в этого бота, списание — со счёта клиента.
          <span v-if="info.numbers.length">У бота уже есть номер — второй нужен, только если клиент просил.</span>
        </div>

        <div class="text-right">
          <b-button variant="light" class="mr-2" :disabled="saving" @click="$emit('close')">Отмена</b-button>
          <b-button v-if="candidate" variant="primary" :disabled="saving" @click="assign">
            <b-spinner v-if="saving" small class="mr-1"></b-spinner>Присвоить {{ fmtPhone(candidate.number) }}
          </b-button>
        </div>
      </template>

      <div v-if="result" class="text-right mt-3">
        <b-button variant="primary" @click="$emit('close')">Готово</b-button>
      </div>
    </template>

    <template v-else>
      <b-alert show variant="danger">{{ error }}</b-alert>
      <div class="text-right">
        <b-button variant="light" @click="$emit('close')">Закрыть</b-button>
      </div>
    </template>
  </div>
</template>
